// src/pages/Dashboard/Admin/DetailsAdherents/components/DossierPanel.jsx

import React from "react";
import {
  FaCheckCircle,
  FaExternalLinkAlt,
  FaFileAlt,
  FaGoogleDrive,
  FaTimesCircle,
} from "react-icons/fa";

import DashboardDataTable from "./DashboardDataTable";

import {
  formatDate,
  getBooleanStatusClass,
  isDriveUrl,
  toAbsoluteUrl,
} from "../utils/adherentDashboardUtils";

function StatusBadge({ ok }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ring-1 ${getBooleanStatusClass(
        ok
      )}`}
    >
      {ok ? <FaCheckCircle /> : <FaTimesCircle />}
      {ok ? "Validé" : "Non validé"}
    </span>
  );
}

function LinkButton({ url, label = "Ouvrir" }) {
  if (!url) return <span className="text-slate-400">—</span>;

  const absolute = toAbsoluteUrl(url);
  const Icon = isDriveUrl(url) ? FaGoogleDrive : FaExternalLinkAlt;

  return (
    <a
      href={absolute}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
    >
      <Icon />
      {label}
    </a>
  );
}

export default function DossierPanel({ dossier }) {
  if (!dossier) {
    return (
      <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-5 text-sm font-medium text-slate-600 dark:border-slate-800 dark:bg-slate-950/60 dark:text-slate-300">
        Aucun dossier d’inscription trouvé pour cet adhérent.
      </div>
    );
  }

  const rows = [
    {
      id: "cm",
      document: "Certificat médical",
      status: <StatusBadge ok={!!dossier?.validate_cm} />,
      date: formatDate(dossier?.date_cm),
      url: dossier?.cm_url,
      actionLabel: "Voir le certificat",
    },
    {
      id: "formulaire-a-signer",
      document: "Formulaire licence à signer",
      status: "—",
      date: "—",
      url: dossier?.formulaire_licence_a_signer,
      actionLabel: "Ouvrir",
    },
    {
      id: "formulaire-signe",
      document: "Formulaire licence signé",
      status: "—",
      date: "—",
      url:
        dossier?.formulaire_licence_signe ||
        dossier?.formulaire_licence_signee,
      actionLabel: "Ouvrir",
    },
    {
      id: "licence",
      document: "Licence / assurance",
      status: <StatusBadge ok={!!dossier?.validate_licence} />,
      date: "—",
      url: dossier?.licence_url || dossier?.licence_assurance_url,
      actionLabel: "Voir la licence",
    },
    {
      id: "drive",
      document: "Dossier Google Drive",
      status: "—",
      date: "—",
      url: dossier?.lien_drive,
      actionLabel: "Ouvrir Drive",
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/60">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
            Certificat médical
          </p>
          <div className="mt-2">
            <StatusBadge ok={!!dossier?.validate_cm} />
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/60">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
            Licence
          </p>
          <div className="mt-2">
            <StatusBadge ok={!!dossier?.validate_licence} />
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/60">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
            Règlement
          </p>
          <div className="mt-2">
            <StatusBadge ok={!!dossier?.validate_reglement} />
          </div>
        </div>
      </div>

      <DashboardDataTable
        minWidth="900px"
        columns={[
          {
            key: "document",
            label: "Document",
            render: (row) => (
              <span className="inline-flex items-center gap-2 font-semibold text-slate-900 dark:text-white">
                <FaFileAlt className="text-indigo-500" />
                {row.document}
              </span>
            ),
          },
          {
            key: "status",
            label: "Statut",
            render: (row) => row.status,
          },
          {
            key: "date",
            label: "Date",
            render: (row) => row.date,
          },
          {
            key: "url",
            label: "Lien",
            render: (row) => (
              <LinkButton url={row.url} label={row.actionLabel} />
            ),
          },
        ]}
        rows={rows}
        emptyMessage="Aucun document."
      />
    </div>
  );
}